
class ContentEditingCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  // Handle generic content editing (text fields, descriptions, etc.)
  async handleContentEditing(data) {
    console.log("📝 ContentEditingCapability: Handling content editing:", data);
    const { fieldName, newContent, programIndex, targetPath } = data;

    // Handle different content types
    switch (fieldName) {
      case "shortSummary":
        this.setters.setShortSummary(newContent);
        break;
      case "goalStatement":
        this.setters.setGoalStatement(newContent);
        break;
      case "problemStatement":
        this.setters.setProblemStatement(newContent);
        break;
      case "targetStatement":
        this.setters.setTargetStatement(newContent);
        break;
      case "objectivesStatement":
        this.setters.setObjectivesStatement(newContent);
        break;
      case "deliverablesStatement":
        this.setters.setDeliverablesStatement(newContent);
        break;
      case "metricsStatement":
        this.setters.setMetricsStatement(newContent);
        break;
      case "stakeholdersCustomersInvolvement":
        this.setters.setStakeholdersCustomersInvolvement(newContent);
        break;
      default:
        console.warn("Unknown content field:", fieldName);
        return { success: false, message: "Unknown content field" };
    }

    this.setters.setIsSaved(false);
    return { success: true, message: `${fieldName} updated successfully` };
  }

  // Handle program-specific content modifications
  async handleProgramContentModification(data) {
    console.log("🔧 ContentEditingCapability: Handling program content modification:", data);
    const { programIndex, field, newContent, operation } = data;

    const updateProgramField = (programs) => {
      const updatedPrograms = [...programs];
      if (updatedPrograms[programIndex]) {
        switch (operation) {
          case "update":
            updatedPrograms[programIndex][field] = newContent;
            break;
          case "append":
            updatedPrograms[programIndex][field] += newContent;
            break;
          case "prepend":
            updatedPrograms[programIndex][field] = newContent + updatedPrograms[programIndex][field];
            break;
          default:
            updatedPrograms[programIndex][field] = newContent;
        }
      }
      return updatedPrograms;
    };

    // Update both state arrays
    this.setters.setFinalProgramSequence(updateProgramField);
    this.setters.setSelectedPrograms(updateProgramField);
    this.setters.setIsSaved(false);

    return { 
      success: true, 
      message: `Program ${field} ${operation}ed successfully`,
      updatedContent: newContent 
    };
  }

  // Handle bulk operations on multiple content elements
  async handleBulkContentOperations(data) {
    console.log("📦 ContentEditingCapability: Handling bulk content operations:", data);
    const { operations } = data;
    const results = [];

    operations.forEach((op, index) => {
      try {
        let result;
        switch (op.type) {
          case "content_editing":
            result = this.handleContentEditing(op.data);
            break;
          case "program_content_modification":
            result = this.handleProgramContentModification(op.data);
            break;
          default:
            result = { success: false, message: `Unknown operation type: ${op.type}` };
        }
        results.push({ index, success: result.success, message: result.message });
      } catch (error) {
        results.push({ index, success: false, message: error.message });
      }
    });

    const successCount = results.filter(r => r.success).length;
    return {
      success: successCount > 0,
      message: `Bulk operation completed: ${successCount}/${operations.length} successful`,
      results
    };
  }

  // Handle AI-generated content insertion
  async handleAIContentGeneration(data) {
    console.log("🤖 ContentEditingCapability: Handling AI content generation:", data);
    const { contentType, targetField, programIndex, generationPrompt } = data;

    // This would integrate with your AI generation services
    // For now, returning a placeholder implementation

    const generatedContent = `AI-generated content for ${contentType}: ${generationPrompt}`;

    // Apply the generated content using existing handlers
    if (programIndex !== undefined) {
      return this.handleProgramContentModification({
        programIndex,
        field: targetField,
        newContent: generatedContent,
        operation: "update"
      });
    } else {
      return this.handleContentEditing({
        fieldName: targetField,
        newContent: generatedContent
      });
    }
  }
}

module.exports = ContentEditingCapability;
